// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AccessGrid.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * AccessGrid component
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import EventsUtils from 'EventUtils';
import { propTypes as MobxPropTypes } from 'mobx-react';
import { merge, map, values, getOr } from 'lodash/fp';

import CONSTANT from 'logic/data/Izenda.AccessConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import OverflowText from 'share/components/controls/Izenda.OverflowText';
import DataGridSearch from 'share/components/controls/DataGrid/Izenda.DataGridSearch';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import Button from 'share/components/controls/Izenda.Button';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import SharePermissionComponent from './Izenda.SharePermissionComponent';
import DropDown from 'share/components/controls/Izenda.DropDown';
import action from './Izenda.AccessGridAction';

const notifyChange = EventsUtils.notifyChange;
const ValidatorDropDown = Validator()(DropDown);

@connect(action)
export default class AccessGrid extends Component {
  static propTypes = {
    store: MobxPropTypes.observableObject,
    users: MobxPropTypes.arrayOrObservableArray,
    roles: MobxPropTypes.arrayOrObservableArray,
    tableDef: PropTypes.object,
    searchOptions: PropTypes.array
  };

  goToPage = (page: number) => {
    const { store } = this.props;
    store.goToPage(page);
  };

  onPageSizeChange = (value: number) => {
    const { store } = this.props;
    store.onPageSizeChange(value);
  };

  /**
   * Render select cell
   * @param  {Object} data
   * @param  {string} key
   * @param  {number} rowIndex
   * @param  {string} column
   * @return {object}   React components
   */
  renderSelectCell = (data: any, key: string, rowIndex: number, column: any): any => {
    return (
      <input
        type="checkbox"
        readOnly={this.props.readOnly}
        value={data[key]}
        checked={data[key]}
        onChange={notifyChange(this.props.onChangeSelectCell.bind(null, data), EventsUtils.CHECKBOX)}
      />
    );
  };

  /**
   * Render text cell
   */
  renderTextCell = (data: any, key: string, rowIndex: number, column: any): any => {
    const text = data[key] || '';
    return <OverflowText tooltip={text} text={text} readOnly={this.props.readOnly} />;
  };

  /**
   * Render action cell
   * @param  {Object} data
   * @param  {string} key
   * @param  {number} rowIndex
   * @param  {string} column
   * @return {object}   React components
   */
  renderAction = (data: any, key: string, rowIndex: number, column: any): any => {
    const localization = this.props.localization;

    return (
      <div>
        <a>
          <span
            title={localization.getText('ACCESS_LABEL_DELETE_ACCESS')}
            className="icon-cancel"
            onClick={!this.props.readOnly && this.props.deleteAccess.bind(null, data)}
            value={rowIndex}
          />
        </a>
      </div>
    );
  };

  /**
   * Render share with cell
   * @param  {Object} data
   * @param  {string} key
   * @param  {number} rowIndex
   * @param  {string} column
   * @return {object}   React components
   */
  renderShareWith = (data: any, key: string, rowIndex: number, column: any): any => {
    const props = this.props;

    return (
      <SharePermissionComponent
        isGlobal={props.isGlobal}
        accessors={data.accessors}
        model={props.store.model}
        data={data}
        store={props.store}
        change={this.props.changeShareWith.bind(null, data)}
        users={props.users}
        roles={props.roles}
        loadUsers={props.loadUsers}
        loadRoles={props.loadRoles}
        readOnly={this.props.readOnly}
      />
    );
  };

  /**
   * Render access right cell
   * @param  {Object} data
   * @param  {string} key
   * @param  {number} rowIndex
   * @param  {string} column
   * @return {object}   React components
   */
  renderAccessRights = (data: any, key: string, rowIndex: number, column: any): any => {
    const props = this.props;
    const { store: { model } } = props;
    const dataOptions = model.getAccessRightByKey(key);
    key = String.format('{0}Id', key);

    return (
      <ValidatorDropDown
        validations={CONSTANT.CONSTRAINTS.ACCESS_RIGHT}
        blankOption
        options={dataOptions}
        value={data[key]}
        valueField="id"
        labelField="name"
        field={key}
        group={data.tempId}
        onChange={this.props.changeAccessRight.bind(null, data, key)}
        disabled={this.props.readOnly}
      />
    );
  };

  /**
   * Render columns component
   */
  renderColumns = (): any => {
    const props = this.props;
    const { tableDef, store: { model } } = props;

    const checkBoxSelectAllProps = {
      onChange: notifyChange(props.checkAll, EventsUtils.CHECKBOX),
      type: 'checkbox',
      checked: model.hasSelectAllActiveAccess,
      disabled: model.hasNoActiveAccess || props.readOnly
    };

    const selectRowDefinition = merge(
      tableDef['selected'],
      {
        headerComponent: Input,
        headerComponentProps: checkBoxSelectAllProps
      },
      {}
    );

    return map(item => {
      switch (item.key) {
        case 'selected':
          return <Column {...selectRowDefinition} cell={this.renderSelectCell} />;
        case 'shareWith':
          return <Column {...item} cell={this.renderShareWith} />;
        case 'dashboardAccessRight':
        case 'reportAccessRight':
        case 'accessRight':
          return <Column {...item} cell={this.renderAccessRights} />;
        case 'actions':
          return <Column {...item} cell={this.renderAction} />;
        default:
          return <Column {...item} cell={this.renderTextCell} />;
      }
    }, values(tableDef));
  };

  /**
   * Render table component
   */
  renderTable = (): any => {
    const props = this.props;
    const { store: { model } } = props;
    const pageInfo = model.pageInfo;

    return (
      <Table dataSource={model.visibleAccessList} onSortChange={props.onSortChange} sortInfo={pageInfo.colSortDirs}>
        {this.renderColumns()}
      </Table>
    );
  };

  /**
   * Render component
   */
  render() {
    const props = this.props;
    const { store: { model } } = props;
    const pageInfo = model.pageInfo;

    const label =
      props.accessMode === CONSTANT.ACCESS_MODE.TENANT
        ? CONSTANT.BUTTON.ADD_DEFAULT_ACCESS
        : CONSTANT.BUTTON.ADD_SHARING_LABEL;
    const currentPageSize = getOr(0, 'length', model.visibleAccessList);

    return (
      <GridLayout>
        <GridLayout.Row className="u_borderAll  u-bgWhite u_noMarginAll u_paddingTopBottom">
          <GridLayout.Col cols="7" className="u_noBorder">
            <DataGridSearch
              options={props.searchOptions}
              value={pageInfo.searchProperty}
              onSearch={props.search}
              onSearchPropertiesChange={props.updateSearchCriteria}
            />
          </GridLayout.Col>
          <GridLayout.Col cols="5" className="u_textRight u_noBorder">
            <div className="izenda-ActionBarRight">
              <Button label={label} onClick={props.onClickAdd} disabled={props.readOnly} />
              <Button
                className="icon-trash-empty"
                disabled={!model.hasActiveInstancesSelected || props.readOnly}
                onClick={props.onClickDelete}
              />
            </div>
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row className="u_noBorder">
          <GridLayout.Col cols="12" className="u_noBorder">
            {this.renderTable()}
          </GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row className="u_noBorder">
          <GridLayout.Col cols="12" className="u_noBorder">
            <Paginator
              pageInfo={pageInfo}
              onChange={this.goToPage}
              isDirty={props.checkDirty}
              currentPageSize={currentPageSize}
              onDropdownChange={this.onPageSizeChange}
            />
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Access/Izenda.AccessGrid.jsx